import {useState, type FormEvent} from 'react'
import {useNavigate} from 'react-router-dom'
import {Mail, Loader2, AlertCircle, ArrowLeft, CheckCircle} from 'lucide-react'
import * as API from '../../../api'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'

interface ForgotPasswordFormProps {
  // opcional: mantém compatibilidade caso alguém ainda passe callback
  onBack?: () => void
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const DEFAULT_ERROR_MESSAGE =
  'Desculpe, ocorreu um erro. Por favor, tente novamente.'

export function ForgotPasswordForm({onBack}: ForgotPasswordFormProps) {
  const navigate = useNavigate()

  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [touched, setTouched] = useState(false)

  const emailTrimmed = email.trim()

  const emailError =
    touched && !emailTrimmed
      ? 'O preenchimento do e-mail é necessário'
      : touched && !EMAIL_REGEX.test(emailTrimmed)
        ? 'Formato de e-mail incorreto'
        : ''

  const isValid = EMAIL_REGEX.test(emailTrimmed)

  const handleBack = () => {
    if (typeof onBack === 'function') {
      onBack()
      return
    }
    navigate('../login') // /auth/forgot-password -> /auth/login
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setTouched(true)

    if (!isValid || loading) return

    setLoading(true)
    setError('')

    try {
      const data: any = await API.forgotPassword(emailTrimmed)

      if (data?.success) {
        setSuccess(true)
        return
      }

      // Mesma regra do código antigo: email não cadastrado
      setError(
        'Esse e-mail não está cadastrado. Confira se você digitou o endereço corretamente.'
      )
    } catch {
      setError(DEFAULT_ERROR_MESSAGE)
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div
        className="w-full max-w-[520px] animate-card-entry translate-y-4 text-[1.05rem] leading-relaxed font-[-apple-system,BlinkMacSystemFont,'Segoe_UI',Roboto,'Helvetica_Neue',Arial,sans-serif] box-border"
        role="main"
      >
        <div className="bg-white/[0.92] backdrop-blur-[24px] border border-white/70 rounded-[22px] px-8 py-10 sm:px-11 sm:py-12 shadow-[0_6px_10px_-2px_rgba(0,50,120,0.05),0_28px_55px_-10px_rgba(0,50,120,0.10),0_0_0_1px_rgba(0,80,180,0.03)]">
          <div className="flex flex-col items-center mb-8">
            <img
              src={toAbsoluteUrl('/media/logos/logo-wide.png')}
              alt="Compra Agora Interno - Logo"
              className="h-14 w-auto mb-3 object-contain"
            />
            <p className="text-sm sm:text-[0.95rem] text-slate-500 text-center">
              Recuperação de acesso
            </p>
          </div>

          <div
            className="flex items-start gap-3 px-5 py-4 mb-6 bg-emerald-50 border border-emerald-200 rounded-xl leading-normal"
            role="status"
          >
            <CheckCircle
              size={22}
              className="shrink-0 text-emerald-600 mt-0.5"
            />
            <div>
              <p className="font-semibold text-emerald-900 text-[0.98rem]">
                Solicitação enviada com sucesso
              </p>
              <p className="text-emerald-800 text-[0.92rem] mt-1 leading-relaxed">
                Verifique sua caixa de entrada e siga as instruções para redefinir
                sua senha.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleBack}
            className="login-submit w-full py-4 mt-2 text-[1.05rem] font-semibold text-white bg-gradient-to-br from-[#0066ff] to-[#0055dd] border-none rounded-xl cursor-pointer transition-all duration-200 relative overflow-hidden leading-normal shadow-[0_10px_20px_-10px_rgba(0,102,255,0.65)] hover:shadow-[0_16px_28px_-14px_rgba(0,102,255,0.75)] hover:-translate-y-[1px] active:translate-y-0"
          >
            Voltar para o login
          </button>

          <div className="mt-7 pt-6 border-t border-slate-100">
            <p className="text-[0.9rem] text-slate-400 text-center font-light">
              Acesso restrito a usuários autorizados
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className="w-full max-w-[520px] animate-card-entry translate-y-4 text-[1.05rem] leading-relaxed font-[-apple-system,BlinkMacSystemFont,'Segoe_UI',Roboto,'Helvetica_Neue',Arial,sans-serif] box-border"
      role="main"
    >
      <div className="bg-white/[0.92] backdrop-blur-[24px] border border-white/70 rounded-[22px] px-8 py-10 sm:px-11 sm:py-12 shadow-[0_6px_10px_-2px_rgba(0,50,120,0.05),0_28px_55px_-10px_rgba(0,50,120,0.10),0_0_0_1px_rgba(0,80,180,0.03)]">
        <div className="flex flex-col items-center mb-8">
          <img
            src={toAbsoluteUrl('/media/logos/logo-wide.png')}
            alt="Compra Agora Interno - Logo"
            className="h-14 w-auto mb-3 object-contain"
          />
          <p className="text-sm sm:text-[0.95rem] text-slate-500 text-center">
            Recuperação de acesso
          </p>
        </div>

        <div className="text-center mb-7">
          <h1 className="text-[1.2rem] font-semibold text-slate-900">
            Esqueceu sua senha?
          </h1>
          <p className="text-[0.95rem] text-slate-500 mt-2 font-light leading-relaxed px-2">
            Digite seu e-mail para receber instruções de acesso.
          </p>
        </div>

        {error && (
          <div
            className="flex items-center gap-2.5 px-4 py-3.5 mb-6 text-[0.95rem] text-red-700 bg-red-50 border border-red-200 rounded-xl leading-snug"
            role="alert"
          >
            <AlertCircle size={18} className="shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} noValidate className="space-y-6">
          <div className="flex flex-col">
            <label
              htmlFor="forgot-email"
              className="block text-[0.95rem] font-semibold text-slate-700 mb-2 leading-snug"
            >
              E-mail
            </label>

            <div
              className={`login-wrapper ${emailError ? 'wrapper-error' : ''} relative flex items-center bg-slate-50/80 rounded-xl transition-all duration-200 ${
                emailError
                  ? 'border-[1.5px] border-red-500'
                  : 'border-[1.5px] border-slate-200 hover:border-slate-300 focus-within:border-blue-400 focus-within:ring-4 focus-within:ring-blue-100/70'
              }`}
            >
              <Mail
                size={20}
                className="login-icon absolute left-4 text-slate-400 pointer-events-none transition-colors duration-200"
              />
              <input
                id="forgot-email"
                type="email"
                placeholder="Digite aqui seu e-mail"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => setTouched(true)}
                autoComplete="email"
                autoFocus
                aria-invalid={!!emailError}
                aria-describedby={emailError ? 'forgot-email-error' : undefined}
                className="login-input w-full py-4 pl-12 pr-4 text-[1rem] text-slate-800 bg-transparent border-none outline-none leading-normal placeholder:text-slate-400"
              />
            </div>

            {emailError && (
              <p
                id="forgot-email-error"
                className="text-[0.9rem] text-red-600 mt-2 pl-0.5 leading-snug"
                role="alert"
              >
                {emailError}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-3">
            <button
              type="submit"
              disabled={loading || !isValid}
              className={`login-submit w-full py-4 text-[1.05rem] font-semibold text-white bg-gradient-to-br from-[#0066ff] to-[#0055dd] border-none rounded-xl cursor-pointer transition-all duration-200 relative overflow-hidden leading-normal shadow-[0_10px_20px_-10px_rgba(0,102,255,0.65)] hover:shadow-[0_16px_28px_-14px_rgba(0,102,255,0.75)] hover:-translate-y-[1px] active:translate-y-0 ${
                loading || !isValid
                  ? 'opacity-70 cursor-not-allowed hover:translate-y-0'
                  : ''
              }`}
              aria-busy={loading}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <Loader2 size={20} className="animate-spin" />
                  Aguarde, por favor...
                </span>
              ) : (
                'Confirmar'
              )}
            </button>

            <button
              type="button"
              onClick={handleBack}
              className="login-back flex items-center justify-center gap-2 w-full py-4 text-[1rem] font-semibold text-slate-600 bg-white/50 hover:bg-white/70 border-[1.5px] border-slate-200 rounded-xl cursor-pointer transition-all duration-200 leading-normal focus:outline-none focus-visible:ring-4 focus-visible:ring-blue-100/80"
            >
              <ArrowLeft size={18} />
              Voltar para o login
            </button>
          </div>
        </form>

        <div className="mt-7 pt-6 border-t border-slate-100">
          <p className="text-[0.9rem] text-slate-400 text-center font-light">
            Acesso restrito a usuários autorizados
          </p>
        </div>
      </div>
    </div>
  )
}
